import { useMemo } from "react";
import Plot from "react-plotly.js";

import { useChartColors } from "../../chartTheme";

const axisBase = {
  gridcolor: "#e5e7eb",
  linecolor: "#d1d5db",
  tickcolor: "#9ca3af",
  tickfont: { size: 10 },
  zeroline: false,
  showspikes: true,
  spikemode: "across",
  spikecolor: "#9ca3af",
  spikethickness: 1,
  spikedash: "dot",
};

export default function CombinedMECO({ members = [], height = "420px" }) {
  const { COMBINED_MECO_PALETTE: PALETTE } = useChartColors();
  const traces = useMemo(() => {
    const out = [];
    members.forEach((member) => {
      const design = member.blade_design;
      const palette = PALETTE[member.position % PALETTE.length];
      const lines = design?.construction_lines || [];
      const zOff = member.z_offset || 0;
      const dname = design?.name ?? "";
      const mz = !!member.mirror_z;  // mirror about x-y plane: z → -z

      lines.forEach((cl) => {
        if (cl.name !== "HUB" && cl.name !== "SHROUD") return;
        if (!cl.z_coords || !cl.r_coords) return;
        const isHub = cl.name === "HUB";
        const zData = cl.z_coords.map(z => (mz ? -z : z) + zOff);
        out.push({
          // x = r, y = z  (matches individual blade H-S orientation)
          x: cl.r_coords,
          y: zData,
          mode: "lines",
          name: `${dname} · ${isHub ? "Hub" : "Shroud"}`,
          line: {
            color: isHub ? palette.hub : palette.shroud,
            dash: isHub ? "solid" : "dash",
            width: 2,
          },
        });
      });
    });
    return out;
  }, [members]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!traces.length) {
    return (
      <div className="flex items-center justify-center h-full min-h-[200px] text-gray-500 text-sm">
        No meridional contour data — add members with MECO construction lines.
      </div>
    );
  }

  return (
    <Plot
      data={traces}
      layout={{
        autosize: true,
        margin: { t: 10, r: 14, b: 48, l: 56 },
        xaxis: {
          ...axisBase,
          title: { text: "r [mm]", font: { size: 11 } },
        },
        yaxis: {
          ...axisBase,
          title: { text: "z [mm]", font: { size: 11 } },
          scaleanchor: "x",
          scaleratio: 1,
        },
        legend: {
          font: { size: 9 },
          bgcolor: "rgba(255,255,255,0.88)",
          bordercolor: "#e5e7eb",
          borderwidth: 1,
          x: 0,
          xanchor: "left",
          y: 1,
          yanchor: "top",
        },
        paper_bgcolor: "transparent",
        plot_bgcolor: "#f9fafb",
        font: { size: 11 },
        dragmode: "pan",
        hovermode: "closest",
      }}
      config={{
        displayModeBar: "hover",
        modeBarButtons: [["zoom2d", "pan2d", "autoScale2d", "resetScale2d", "toImage"]],
        scrollZoom: true,
        responsive: true,
        displaylogo: false,
      }}
      style={{ width: "100%", height }}
      useResizeHandler
    />
  );
}
